import * as THREE from 'three'
import { InstancedMeshRaycastIndex } from '../../../raycast-acceleration/instancing'
import { ImportedModelInstancedTopologyDisplayEdgeProjection } from '../edge'
import { ImportedModelInstanceIndexSnapshot } from './ImportedModelInstanceIndexSnapshot'
import { ImportedModelInstancedObjectTransformProjection } from './ImportedModelInstancedObjectTransformProjection'
import { ImportedModelInstanceSlotMask, ImportedModelTransparentInstanceMesh } from '../appearance'

/**
 * 持有一个且仅持有一个 Three.js InstancedMesh 的运行时实例组。
 *
 * Geometry 与 Material 继续由原导入结果的资源 scope 持有；Unit 只拥有 InstancedMesh 自身
 * 的 instanceMatrix、bounds 和 GPU 对象生命周期，销毁时不得释放共享 Geometry/Material。
 */
export class ImportedModelInstancedMeshUnit {
  /** 当前实例组的正式渲染对象。 */
  public readonly object: THREE.InstancedMesh

  /** 与正式实例组同生命周期的局部槽位粗筛及共享几何精确拾取。 */
  private readonly raycastIndex: InstancedMeshRaycastIndex

  /** 当前实例组的不可变 GPU 下标快照。 */
  private readonly snapshot: ImportedModelInstanceIndexSnapshot

  /** 与表面 Unit 共享 instanceMatrix 的默认拓扑边线投影。 */
  private readonly topologyDisplayEdgeProjection: ImportedModelInstancedTopologyDisplayEdgeProjection

  /** 计算投影父级逆矩阵时复用的临时矩阵。 */
  private readonly inverseProjectionParentMatrix = new THREE.Matrix4()

  /** 写入单个实例槽时复用的临时矩阵。 */
  private readonly instanceMatrix = new THREE.Matrix4()

  /** 脱离主场景树后仍可解析的 source Mesh 有效世界矩阵。 */
  private readonly sourceWorldMatrix = new THREE.Matrix4()

  /** 上次已提交到 GPU 的矩阵元素缓存，用于跳过无变化的即时渲染。 */
  private readonly cachedMatrixElements: Float64Array

  /** source Mesh 到当前 Unit GPU 槽位的直接索引。 */
  private readonly instanceIndexBySourceMesh = new Map<THREE.Mesh, number>()

  /** source Mesh 到权威 occurrence 变换根的直接索引。 */
  private readonly occurrenceRootBySourceMesh = new Map<THREE.Mesh, THREE.Object3D>()

  /** source Mesh 到单槽派生实例投影对象及局部后缀矩阵的索引。 */
  private readonly matrixProjectionTargetsBySourceMesh = new Map<
    THREE.Mesh,
    Map<THREE.InstancedMesh, ImportedModelInstanceMatrixProjection>
  >()

  /** source Mesh 到普通 Object3D 派生变换投影的索引。 */
  private readonly objectTransformProjectionsBySourceMesh = new Map<
    THREE.Mesh,
    Map<THREE.Object3D, ImportedModelInstancedObjectTransformProjection>
  >()

  /** 写入带局部后缀的派生实例槽时复用的临时矩阵。 */
  private readonly matrixProjectionTargetMatrix = new THREE.Matrix4()

  /** 未指定局部后缀时复用的单位矩阵。 */
  private readonly identityMatrixProjectionSuffix = new THREE.Matrix4()

  /** Unit 是否已经结束生命周期。 */
  private disposed = false
  /** 与正式 Unit 同生命周期的派生缓存释放订阅。 */
  private readonly disposeListeners = new Set<() => void>()
  /** 临时透明与正式实例生命周期分离，按 owner 合并目标槽。 */
  private readonly temporaryOpacityByOwner = new Map<string, ReadonlyMap<THREE.Mesh, number>>()
  /** 当前实际需要透明绘制的单槽投影。 */
  private readonly transparentInstances = new Map<THREE.Mesh, ImportedModelTransparentInstanceMesh>()
  /** 仅在存在临时透明时拥有正式表面/边线遮罩。 */
  private temporarySlotMask: ImportedModelInstanceSlotMask | null = null

  /**
   * 创建离屏 InstancedMeshUnit 并写入全部矩阵。
   *
   * @param slot 已通过资格判定的渲染槽。
   * @param projectionParent 实例投影根最终将挂载到的父对象。
   * @param generation 当前结构代次。
   * @param copySourceWorldMatrix source Mesh 脱离主场景树后的世界矩阵读取端口。
   * @param resolveSourceOriginalParent source Mesh 脱离前业务父对象的读取端口。
   */
  public constructor(
    private readonly slot: PuppyCAD.ImportedModelInstancingRenderSlot,
    projectionParent: THREE.Object3D,
    generation: number,
    private readonly copySourceWorldMatrix: (
      sourceMesh: THREE.Mesh,
      targetMatrix: THREE.Matrix4,
    ) => boolean = (sourceMesh, targetMatrix): boolean => {
      sourceMesh.updateWorldMatrix(true, false)
      targetMatrix.copy(sourceMesh.matrixWorld)
      return true
    },
    private readonly resolveSourceOriginalParent: (
      sourceMesh: THREE.Mesh,
    ) => THREE.Object3D | null = sourceMesh => sourceMesh.parent,
  ) {
    this.snapshot = new ImportedModelInstanceIndexSnapshot(slot.members, generation)
    this.cachedMatrixElements = new Float64Array(slot.members.length * 16)
    this.cachedMatrixElements.fill(Number.NaN)
    slot.members.forEach((member, instanceIndex) => {
      this.instanceIndexBySourceMesh.set(member.sourceMesh, instanceIndex)
      this.occurrenceRootBySourceMesh.set(member.sourceMesh, member.occurrenceRoot)
    })
    this.object = new THREE.InstancedMesh(slot.geometry, slot.material, slot.members.length)
    this.object.name = `导入模型实例组-${slot.definitionKey}`
    this.object.matrixAutoUpdate = false
    this.object.matrix.identity()
    this.object.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.raycastIndex = new InstancedMeshRaycastIndex(this.object)

    const seed = slot.members[0]?.sourceMesh
    if (seed) {
      this.object.renderOrder = seed.renderOrder
      this.object.layers.mask = seed.layers.mask
      this.object.castShadow = seed.castShadow
      this.object.receiveShadow = seed.receiveShadow
      this.object.frustumCulled = seed.frustumCulled
    }
    this.topologyDisplayEdgeProjection = new ImportedModelInstancedTopologyDisplayEdgeProjection(
      slot,
      this,
    )
    this.syncTransforms(projectionParent)
  }

  /**
   * 按当前模型显示模式启停实例化默认拓扑边线。
   *
   * @param enabled 是否显示默认边线。
   * @param color 当前主题要求的边线颜色。
   * @returns 当前 Unit 拓扑等价且能够实例化边线时返回 true。
   */
  public setTopologyDisplayEdgesEnabled(
    enabled: boolean,
    color: THREE.ColorRepresentation,
  ): boolean {
    this.assertAvailable()
    const previousEdges = this.topologyDisplayEdgeProjection.getObject()
    if (previousEdges) this.temporarySlotMask?.unbindObject(previousEdges)
    const handled = this.topologyDisplayEdgeProjection.setEnabled(enabled, color)
    const edges = this.topologyDisplayEdgeProjection.getObject()
    if (edges) this.temporarySlotMask?.bindObject(edges)
    this.transparentInstances.forEach(instance => instance.syncEdges())
    return handled
  }

  /** @returns 当前显示模式是否已启用正式实例边线。 */
  public hasTopologyDisplayEdges(): boolean { return this.topologyDisplayEdgeProjection.getObject() !== null }
  /** @returns 临时边线与正式边线共同使用的主题颜色。 */
  public getTopologyDisplayEdgeColor(): THREE.ColorRepresentation { return this.topologyDisplayEdgeProjection.getColor() }

  /** @param matrix 临时单槽矩阵。 @returns 借用已校验拓扑创建的临时边线。 */
  public createTemporaryTopologyDisplayEdges(matrix: THREE.InstancedBufferAttribute) {
    return this.topologyDisplayEdgeProjection.createTemporary(matrix)
  }

  /**
   * 更新一个外观 owner 的本 Unit 目标；只改差异槽，不改变 source、矩阵或注册代次。
   * @param ownerToken 命令外观所有者。
   * @param sources 当前目标 source 集合。
   * @param opacity 临时透明度。
   * @returns 是否改变实际显示。
   */
  public setTemporaryOpacity(ownerToken: string, sources: readonly THREE.Mesh[], opacity: number): boolean {
    // 外部领域可先结束 Unit；注册表随后归还 owner 时不得复活已销毁投影或抛清理异常。
    if (this.disposed) return false
    const next = new Map(sources.filter(source => this.instanceIndexBySourceMesh.has(source)).map(source => [source, opacity]))
    const previous = this.temporaryOpacityByOwner.get(ownerToken)
    if ((previous?.size ?? 0) === next.size && [...next].every(([source, value]) => previous?.get(source) === value)) return false
    const effective = new Map<THREE.Mesh, number>()
    this.temporaryOpacityByOwner.forEach((values, owner) => {
      if (owner !== ownerToken) values.forEach((value, source) => {
        effective.set(source, Math.min(value, effective.get(source) ?? 1))
      })
    })
    next.forEach((value, source) => effective.set(source, Math.min(value, effective.get(source) ?? 1)))
    const prepared = new Map<THREE.Mesh, ImportedModelTransparentInstanceMesh>()
    let preparedMask: ImportedModelInstanceSlotMask | null = null
    try {
      if (effective.size && !this.temporarySlotMask) {
        preparedMask = new ImportedModelInstanceSlotMask(this.object.count)
        preparedMask.bindObject(this.object)
        const edges = this.topologyDisplayEdgeProjection.getObject()
        if (edges) preparedMask.bindObject(edges)
      }
      effective.forEach((value, source) => {
        if (!this.transparentInstances.has(source)) {
          prepared.set(source, new ImportedModelTransparentInstanceMesh(this, source, value))
        }
      })
    } catch (error) {
      // 先准备资源再发布 owner；失败只归还本轮资源，旧 owner 及旧显示必须保持。
      prepared.forEach(instance => instance.dispose())
      preparedMask?.dispose()
      throw error
    }
    if (preparedMask) this.temporarySlotMask = preparedMask
    if (next.size) this.temporaryOpacityByOwner.set(ownerToken, next)
    else this.temporaryOpacityByOwner.delete(ownerToken)
    prepared.forEach((instance, source) => this.transparentInstances.set(source, instance))
    effective.forEach((value, source) => {
      this.transparentInstances.get(source)!.setOpacity(value)
      this.temporarySlotMask?.setVisible(this.instanceIndexBySourceMesh.get(source)!, false)
    })
    this.transparentInstances.forEach((instance, source) => {
      if (effective.has(source)) return
      instance.dispose()
      this.transparentInstances.delete(source)
      this.temporarySlotMask?.setVisible(this.instanceIndexBySourceMesh.get(source)!, true)
    })
    if (!effective.size) { this.temporarySlotMask?.dispose(); this.temporarySlotMask = null }
    return true
  }

  /** 结束所有临时外观，归还正式材质与独占 GPU 资源。 */
  private clearTemporaryOpacity(): void {
    this.transparentInstances.forEach(instance => instance.dispose())
    this.transparentInstances.clear()
    this.temporaryOpacityByOwner.clear()
    this.temporarySlotMask?.dispose()
    this.temporarySlotMask = null
  }

  /**
   * 同步全部 occurrence 的世界变换到实例槽。
   *
   * 矩阵统一换算到实例投影父级局部空间；两个临时 Matrix4 持续复用，避免移动和频繁重绘
   * 期间制造垃圾对象。
   *
   * @param projectionParent 实例投影根最终所在的父对象。
   */
  public syncTransforms(projectionParent: THREE.Object3D): void {
    this.assertAvailable()
    // 初次发布前一次性刷新权威装配子树；后续增量更新由 Coordinator 只刷新变化对象子树。
    projectionParent.updateWorldMatrix(true, true)
    this.inverseProjectionParentMatrix.copy(projectionParent.matrixWorld).invert()

    let changed = false
    for (const member of this.slot.members) {
      changed = this.syncSourceMeshTransform(
        member.sourceMesh,
        this.inverseProjectionParentMatrix,
      ) || changed
    }
    if (!changed) {
      return
    }
    // 首次创建 GPU buffer 必须上传完整矩阵数组，不能遗留逐槽增量范围给后续批次。
    this.object.instanceMatrix.clearUpdateRanges()
    this.commitMatrixChanges()
  }

  /**
   * 只把一个发生变化的 source Mesh 世界矩阵写入其 GPU 槽位。
   *
   * 该入口由 Coordinator 的变换反向索引调用，不遍历同 Unit 的其它 occurrence；投影父级
   * 逆矩阵由同一增量批次统一计算并传入，避免每个槽位重复求逆。
   *
   * @param sourceMesh 已发生世界变换变化的 source Mesh。
   * @param inverseProjectionParentMatrix 投影父级世界矩阵的逆矩阵。
   * @returns 当前 GPU 槽位是否实际发生变化。
   */
  public syncSourceMeshTransform(
    sourceMesh: THREE.Mesh,
    inverseProjectionParentMatrix: THREE.Matrix4,
  ): boolean {
    this.assertAvailable()
    const instanceIndex = this.instanceIndexBySourceMesh.get(sourceMesh)
    if (instanceIndex === undefined) {
      return false
    }
    if (!this.copySourceWorldMatrix(sourceMesh, this.sourceWorldMatrix)) {
      return false
    }
    this.instanceMatrix.multiplyMatrices(
      inverseProjectionParentMatrix,
      this.sourceWorldMatrix,
    )
    /** 普通拓扑宿主不使用 GPU 槽，但必须消费同一次权威 source 世界矩阵刷新。 */
    this.syncObjectTransformProjections(sourceMesh, this.sourceWorldMatrix)
    if (!this.updateMatrixCache(instanceIndex, this.instanceMatrix.elements)) {
      return false
    }
    this.object.setMatrixAt(instanceIndex, this.instanceMatrix)
    this.raycastIndex.markInstanceDirty(instanceIndex)
    this.syncMatrixProjectionTargets(sourceMesh, this.instanceMatrix)
    // Three.js 会在下一次上传时合并这些范围，只提交本轮真正变化的 4x4 矩阵片段。
    this.object.instanceMatrix.addUpdateRange(instanceIndex * 16, 16)
    return true
  }

  /**
   * 提交本轮已经写入的矩阵槽并更新当前 Unit 的空间包围。
   *
   * Coordinator 会对同一 Unit 的多个变化槽位先全部写入，再只调用一次该方法。
   */
  public commitMatrixChanges(): void {
    this.assertAvailable()
    this.object.instanceMatrix.needsUpdate = true
    this.object.computeBoundingBox()
    this.object.computeBoundingSphere()
    this.raycastIndex.commitMatrixChanges()
  }

  /**
   * 读取正式实例空间修订，复用拾取索引的几何与矩阵版本校验。
   * @returns 当前空间修订，不修改正式实例矩阵或触发渲染。
   */
  public getSpatialBoundsRevision(): number {
    this.assertAvailable()
    return this.raycastIndex.getBoundsRevision()
  }

  /**
   * 按业务来源复制已有局部槽位 AABB，避免相机再次变换全部几何包围盒。
   * @param sourceMesh 当前 Unit 管理的业务来源。
   * @param target 消费者复用的边界缓冲。
   * @returns 是否存在可靠槽位边界；读取前须校验本 Unit 的空间修订。
   */
  public copySourceMeshLocalBounds(sourceMesh: THREE.Mesh, target: THREE.Box3): boolean {
    this.assertAvailable()
    const instanceIndex = this.instanceIndexBySourceMesh.get(sourceMesh)
    target.makeEmpty()
    return instanceIndex !== undefined && this.raycastIndex.copyInstanceBounds(instanceIndex, target)
  }

  /** @returns 显式调试读取的拾取工作量；不改变或暴露持久化业务身份。 */
  public getRaycastStats(): InstancedMeshRaycastStats {
    this.assertAvailable()
    return this.raycastIndex.getStats()
  }

  /**
   * 比较并更新一个实例槽的矩阵缓存。
   *
   * @param instanceIndex 当前 GPU 实例下标。
   * @param elements 最新矩阵元素。
   * @returns 与上次提交相比是否发生变化。
   */
  private updateMatrixCache(instanceIndex: number, elements: readonly number[]): boolean {
    const offset = instanceIndex * 16
    let changed = false
    for (let elementIndex = 0; elementIndex < 16; elementIndex += 1) {
      const nextValue = elements[elementIndex] ?? 0
      if (this.cachedMatrixElements[offset + elementIndex] !== nextValue) {
        changed = true
        this.cachedMatrixElements[offset + elementIndex] = nextValue
      }
    }
    return changed
  }

  /** @returns 当前不可变 GPU 下标快照。 */
  public getSnapshot(): ImportedModelInstanceIndexSnapshot {
    this.assertAvailable()
    return this.snapshot
  }

  /** @returns 当前实例组的只读统计。 */
  public getStats(): PuppyCAD.ImportedModelInstancedMeshUnitStats {
    this.assertAvailable()
    return Object.freeze({
      unitKey: this.slot.unitKey,
      objectUuid: this.object.uuid,
      instanceCount: this.snapshot.getSize(),
      generation: this.snapshot.getGeneration(),
    })
  }

  /** @returns 当前实例组接管的全部 source Mesh。 */
  public listSourceMeshes(): readonly THREE.Mesh[] {
    this.assertAvailable()
    return Object.freeze(this.slot.members.map(member => member.sourceMesh))
  }

  /**
   * 解析 source Mesh 对应的权威 occurrence 变换根。
   *
   * @param sourceMesh 当前 Unit 接管的业务 Mesh。
   * @returns 当前 occurrence 根；source 不属于本 Unit 时返回 null。
   */
  public resolveOccurrenceRoot(sourceMesh: THREE.Mesh): THREE.Object3D | null {
    this.assertAvailable()
    return this.occurrenceRootBySourceMesh.get(sourceMesh) ?? null
  }

  /**
   * 读取 source Mesh 被实例投影抑制前的业务父对象。
   *
   * topology host 的普通态父子关系属于场景结构职责，Unit 只通过注入端口暴露权威结果，
   * 不直接依赖具体的 source Mesh 抑制控制器。
   *
   * @param sourceMesh 当前 Unit 接管的业务 Mesh。
   * @returns source Mesh 的原业务父对象；source 不属于本 Unit 或父对象不可解析时返回 null。
   */
  public resolveSourceMeshOriginalParent(sourceMesh: THREE.Mesh): THREE.Object3D | null {
    this.assertAvailable()
    if (!this.instanceIndexBySourceMesh.has(sourceMesh)) {
      return null
    }
    return this.resolveSourceOriginalParent(sourceMesh)
  }

  /**
   * 复制 source Mesh 当前有效世界矩阵。
   *
   * 该入口供 topology host 生命周期迁移复用同一权威矩阵读取端口，避免投影器根据 source
   * 是否仍在物理场景树中建立第二套判断规则。
   *
   * @param sourceMesh 当前 Unit 接管的业务 Mesh。
   * @param targetMatrix 接收有效世界矩阵的可复用对象。
   * @returns source 属于本 Unit 且矩阵可解析时返回 true。
   */
  public copySourceMeshWorldMatrix(
    sourceMesh: THREE.Mesh,
    targetMatrix: THREE.Matrix4,
  ): boolean {
    this.assertAvailable()
    if (!this.instanceIndexBySourceMesh.has(sourceMesh)) {
      return false
    }
    return this.copySourceWorldMatrix(sourceMesh, targetMatrix)
  }

  /**
   * 收集指定业务对象范围内由当前 Unit 接管的 source Mesh。
   *
   * @param rootObject occurrence 根、其装配祖先或 source Mesh 自身。
   * @returns 保持实例槽顺序的业务 Mesh 集合。
   */
  public listSourceMeshesForObject(rootObject: THREE.Object3D): readonly THREE.Mesh[] {
    this.assertAvailable()
    return Object.freeze(this.slot.members
      .filter((member) => (
        member.sourceMesh === rootObject
        || this.isDescendantOrSelf(member.occurrenceRoot, rootObject)
      ))
      .map(member => member.sourceMesh))
  }

  /**
   * 刷新脱离 source Mesh 的有效世界矩阵，供包围盒、测量和碰撞等只读空间查询使用。
   *
   * @param sourceMesh 当前 Unit 接管的业务 Mesh。
   * @returns 刷新成功时返回 true。
   */
  public prepareSourceMeshForSpatialRead(sourceMesh: THREE.Mesh): boolean {
    return this.copySourceMeshWorldMatrix(sourceMesh, sourceMesh.matrixWorld)
  }

  /**
   * 把指定 source Mesh 当前已经提交的实例矩阵复制到外部目标矩阵。
   *
   * 该入口用于选中投影等派生渲染对象复用正式实例槽的 Float32 矩阵，禁止调用方重新从
   * source Mesh 世界矩阵计算第二份结果，避免两条渲染路径产生精度与更新时序差异。
   *
   * @param sourceMesh 当前 Unit 接管的 source Mesh。
   * @param targetMatrix 接收实例槽矩阵的可复用目标对象。
   * @returns source Mesh 属于当前 Unit 时返回 true；否则返回 false。
   */
  public copyInstanceMatrixForSourceMesh(
    sourceMesh: THREE.Mesh,
    targetMatrix: THREE.Matrix4,
  ): boolean {
    this.assertAvailable()
    const instanceIndex = this.instanceIndexBySourceMesh.get(sourceMesh)
    if (instanceIndex === undefined) {
      return false
    }
    this.object.getMatrixAt(instanceIndex, targetMatrix)
    return true
  }

  /**
   * 挂接一个需要持续复用指定 source Mesh 实例槽矩阵的单槽派生对象。
   *
   * Unit 在每次增量更新正式槽位时直接同步这些目标，不触发全量 transform 扫描。该能力只
   * 负责矩阵投影，不拥有目标对象的 geometry、material 或生命周期。
   *
   * @param sourceMesh 当前 Unit 接管的 source Mesh。
   * @param target 接收指定槽的派生 InstancedMesh。
   * @param localMatrixSuffix 派生 geometry 本地坐标到 source Mesh 本地坐标的后缀矩阵。
   * @param targetIndex 派生批次目标槽位；默认 0 保持既有单槽调用契约。
   * @returns source Mesh 属于当前 Unit 时返回 true；否则返回 false。
   */
  public attachInstanceMatrixProjection(
    sourceMesh: THREE.Mesh,
    target: THREE.InstancedMesh,
    localMatrixSuffix: THREE.Matrix4 = this.identityMatrixProjectionSuffix,
    targetIndex = 0,
  ): boolean {
    this.assertAvailable()
    if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= target.instanceMatrix.count) {
      throw new Error(`实例矩阵投影目标槽越界：${targetIndex}`)
    }
    if (!this.copyInstanceMatrixForSourceMesh(sourceMesh, this.instanceMatrix)) {
      return false
    }
    let targetBindings = this.matrixProjectionTargetsBySourceMesh.get(sourceMesh)
    if (!targetBindings) {
      targetBindings = new Map<THREE.InstancedMesh, ImportedModelInstanceMatrixProjection>()
      this.matrixProjectionTargetsBySourceMesh.set(sourceMesh, targetBindings)
    }
    const projection = targetBindings.get(target)
    if (projection) {
      projection.localMatrixSuffix.copy(localMatrixSuffix)
      projection.targetIndex = targetIndex
    } else {
      targetBindings.set(target, { localMatrixSuffix: localMatrixSuffix.clone(), targetIndex })
    }
    this.writeMatrixProjectionTarget(target, this.instanceMatrix, localMatrixSuffix, targetIndex)
    return true
  }

  /**
   * 解除单槽派生对象与 source Mesh 实例矩阵的持续联动。
   *
   * @param sourceMesh 原始显示 Mesh。
   * @param target 待解除的派生 InstancedMesh。
   */
  public detachInstanceMatrixProjection(
    sourceMesh: THREE.Mesh,
    target: THREE.InstancedMesh,
  ): void {
    if (this.disposed) {
      return
    }
    const targetBindings = this.matrixProjectionTargetsBySourceMesh.get(sourceMesh)
    if (!targetBindings) {
      return
    }
    targetBindings.delete(target)
    if (targetBindings.size === 0) {
      this.matrixProjectionTargetsBySourceMesh.delete(sourceMesh)
    }
  }

  /**
   * 挂接一个需要持续跟随 source Mesh 世界变换的普通 Object3D 派生对象。
   *
   * 该入口用于 topology pick host 等不能直接使用 InstancedMesh 的语义/显示对象。绑定只负责
   * 变换投影，不拥有目标 geometry、material、显隐或生命周期。
   *
   * @param sourceMesh 当前 Unit 接管的 source Mesh。
   * @param targetObject 需要持续跟随 source Mesh 的普通派生对象。
   * @returns source Mesh 属于当前 Unit 且目标对象已完成绑定时返回 true。
   */
  public attachObjectTransformProjection(
    sourceMesh: THREE.Mesh,
    targetObject: THREE.Object3D,
  ): boolean {
    this.assertAvailable()
    if (!this.instanceIndexBySourceMesh.has(sourceMesh)) {
      return false
    }
    if (!this.copySourceWorldMatrix(sourceMesh, this.sourceWorldMatrix)) {
      return false
    }
    let projections = this.objectTransformProjectionsBySourceMesh.get(sourceMesh)
    if (!projections) {
      projections = new Map<THREE.Object3D, ImportedModelInstancedObjectTransformProjection>()
      this.objectTransformProjectionsBySourceMesh.set(sourceMesh, projections)
    }
    const existingProjection = projections.get(targetObject)
    if (existingProjection) {
      return existingProjection.syncFromSourceWorldMatrix(this.sourceWorldMatrix)
    }
    const projection = new ImportedModelInstancedObjectTransformProjection(
      targetObject,
      this.sourceWorldMatrix,
    )
    projections.set(targetObject, projection)
    return projection.syncFromSourceWorldMatrix(this.sourceWorldMatrix)
  }

  /**
   * 解除普通 Object3D 派生对象与 source Mesh 的变换联动。
   *
   * @param sourceMesh 原始显示 Mesh。
   * @param targetObject 待解除的普通派生对象。
   */
  public detachObjectTransformProjection(
    sourceMesh: THREE.Mesh,
    targetObject: THREE.Object3D,
  ): void {
    if (this.disposed) {
      return
    }
    const projections = this.objectTransformProjectionsBySourceMesh.get(sourceMesh)
    if (!projections) {
      return
    }
    projections.delete(targetObject)
    if (projections.size === 0) {
      this.objectTransformProjectionsBySourceMesh.delete(sourceMesh)
    }
  }

  /**
   * 把变化后的正式槽矩阵同步写入全部单槽派生对象。
   *
   * @param sourceMesh 本轮发生变化的 source Mesh。
   * @param matrix 已经写入正式 InstancedMesh 的同一矩阵对象。
   */
  private syncMatrixProjectionTargets(
    sourceMesh: THREE.Mesh,
    matrix: THREE.Matrix4,
  ): void {
    this.matrixProjectionTargetsBySourceMesh.get(sourceMesh)?.forEach((projection, target) => {
      this.writeMatrixProjectionTarget(target, matrix, projection.localMatrixSuffix, projection.targetIndex)
    })
  }

  /**
   * 同步指定 source Mesh 对应的全部普通 Object3D 派生变换。
   *
   * @param sourceMesh 本轮已更新世界矩阵的 source Mesh。
   * @param sourceWorldMatrix source Mesh 当前有效世界矩阵。
   */
  private syncObjectTransformProjections(
    sourceMesh: THREE.Mesh,
    sourceWorldMatrix: THREE.Matrix4,
  ): void {
    const projections = this.objectTransformProjectionsBySourceMesh.get(sourceMesh)
    if (!projections) {
      return
    }
    projections.forEach((projection, targetObject) => {
      if (!projection.syncFromSourceWorldMatrix(sourceWorldMatrix)) {
        projections.delete(targetObject)
      }
    })
    if (projections.size === 0) {
      this.objectTransformProjectionsBySourceMesh.delete(sourceMesh)
    }
  }

  /**
   * 向单槽派生 InstancedMesh 提交矩阵变化。
   *
   * @param target 目标派生对象。
   * @param matrix 当前正式槽矩阵。
   * @param localMatrixSuffix 派生 geometry 本地坐标到 source Mesh 本地坐标的后缀矩阵。
   * @param targetIndex 派生批次中与 source 固定对应的槽位。
   */
  private writeMatrixProjectionTarget(
    target: THREE.InstancedMesh,
    matrix: THREE.Matrix4,
    localMatrixSuffix: THREE.Matrix4,
    targetIndex: number,
  ): void {
    this.matrixProjectionTargetMatrix.multiplyMatrices(matrix, localMatrixSuffix)
    target.setMatrixAt(targetIndex, this.matrixProjectionTargetMatrix)
    // 合并成一个有界范围：保留本轮先前变化槽，且隐藏缓存长期不渲染时不会累积范围对象。
    const start = targetIndex * 16
    const range = target.instanceMatrix.updateRanges[0]
    if (range) {
      const end = Math.max(range.start + range.count, start + 16)
      range.start = Math.min(range.start, start)
      range.count = end - range.start
    } else target.instanceMatrix.addUpdateRange(start, 16)
    target.instanceMatrix.needsUpdate = true
  }

  /**
   * 判断对象是否为指定根对象或其后代。
   *
   * @param object 待判断对象。
   * @param rootObject 目标根对象。
   * @returns 对象位于该业务子树时返回 true。
   */
  private isDescendantOrSelf(
    object: THREE.Object3D,
    rootObject: THREE.Object3D,
  ): boolean {
    let current: THREE.Object3D | null = object
    while (current) {
      if (current === rootObject) {
        return true
      }
      current = current.parent ?? (current instanceof THREE.Mesh ? this.resolveSourceOriginalParent(current) : null)
    }
    return false
  }

  /**
   * 订阅正式 Unit 销毁，使选择缓存等派生资源不跨模型或跨代次滞留。
   * @param listener 释放回调，不得销毁正式业务资产。
   * @returns 幂等退订函数。
   */
  public onDispose(listener: () => void): () => void {
    this.assertAvailable()
    this.disposeListeners.add(listener)
    return () => { this.disposeListeners.delete(listener) }
  }

  /** 幂等移除实例对象并释放其独占 GPU 状态。 */
  public dispose(): void {
    if (this.disposed) {
      return
    }
    this.clearTemporaryOpacity()
    this.disposed = true
    const releaseErrors: unknown[] = []
    for (const listener of this.disposeListeners) {
      try { listener() } catch (error) { releaseErrors.push(error) }
    }
    this.disposeListeners.clear()
    this.raycastIndex.dispose()
    this.topologyDisplayEdgeProjection.dispose()
    this.matrixProjectionTargetsBySourceMesh.forEach((targetBindings) => {
      targetBindings.forEach((_localMatrixSuffix, target) => {
        target.visible = false
      })
    })
    this.matrixProjectionTargetsBySourceMesh.clear()
    this.objectTransformProjectionsBySourceMesh.clear()
    this.object.removeFromParent()
    this.object.dispose()
    if (releaseErrors.length) throw new AggregateError(releaseErrors, '正式实例组派生缓存释放失败')
  }

  /** Unit 销毁后禁止继续访问旧 GPU 映射。 */
  private assertAvailable(): void {
    if (this.disposed) {
      throw new Error(`导入模型实例组已释放：${this.slot.unitKey}`)
    }
  }
}
